"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import { useFormStatus } from "react-dom";

import { sendMessage } from "@/app/contact/actions";
import { MAX_MESSAGE, MAX_NAME } from "@/app/contact/constants";
import { initialSendState, type FieldErrors } from "@/app/contact/types";

const fieldClass =
  "w-full rounded-lg border border-border-input bg-transparent px-4 py-3 text-base text-foreground placeholder:text-muted transition-[border-color] hover:border-border-hover aria-invalid:border-error aria-invalid:focus-visible:border-error disabled:opacity-60";

const fieldOrder = ["name", "email", "message"] as const;

function firstErrorKey(errors: FieldErrors): keyof FieldErrors | undefined {
  return fieldOrder.find((key) => errors[key]);
}

function SubmitButton({ alerted, shake }: { alerted: boolean; shake: boolean }) {
  const { pending } = useFormStatus();
  const showAlert = alerted && !pending;

  return (
    <button
      type="submit"
      disabled={pending}
      aria-busy={pending}
      className={`inline-flex min-h-11 cursor-pointer touch-manipulation items-center gap-2 rounded-full px-5 text-sm tracking-wide transition-colors duration-500 disabled:cursor-not-allowed disabled:opacity-70 ${
        showAlert
          ? "bg-error/15 text-error hover:bg-error/25"
          : "bg-accent/15 text-accent hover:bg-accent/25"
      } ${shake && showAlert ? "button-shake" : ""}`}
    >
      {pending ? (
        <span
          className="size-3.5 rounded-full border border-accent/30 border-t-accent motion-safe:animate-spin"
          aria-hidden
        />
      ) : null}
      send message
    </button>
  );
}

export function ContactForm() {
  const [state, formAction] = useActionState(sendMessage, initialSendState);
  const [messageLength, setMessageLength] = useState(0);
  const [buttonAlert, setButtonAlert] = useState(false);
  const [buttonShake, setButtonShake] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.status === "idle") {
      return;
    }

    if (state.status === "success") {
      setButtonAlert(false);
      setButtonShake(false);
      formRef.current?.reset();
      setMessageLength(0);
      statusRef.current?.focus();

      return;
    }

    setButtonAlert(true);
    setButtonShake(false);

    const key = firstErrorKey(state.fieldErrors);
    const fieldRefs = {
      name: nameRef,
      email: emailRef,
      message: messageRef,
    };

    if (key) {
      fieldRefs[key].current?.focus();
    } else {
      statusRef.current?.focus();
    }

    let cancelled = false;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!cancelled) {
          setButtonShake(true);
        }
      });
    });
    const timer = window.setTimeout(() => {
      setButtonAlert(false);
      setButtonShake(false);
    }, 2500);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [state]);

  const nameErrorId = state.fieldErrors.name ? "contact-name-error" : undefined;
  const emailErrorId = state.fieldErrors.email
    ? "contact-email-error"
    : undefined;
  const messageErrorId = state.fieldErrors.message
    ? "contact-message-error"
    : undefined;

  return (
    <form
      ref={formRef}
      action={formAction}
      noValidate
      className="mt-10 space-y-4"
      aria-describedby="contact-status"
    >
      <div
        ref={statusRef}
        id="contact-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        tabIndex={-1}
        className={`text-sm outline-none ${
          state.status === "error"
            ? "text-error"
            : state.status === "success"
              ? "text-accent"
              : "sr-only"
        }`}
      >
        {state.message}
      </div>

      <div>
        <label htmlFor="contact-name" className="sr-only">
          Name
        </label>
        <input
          ref={nameRef}
          id="contact-name"
          name="name"
          type="text"
          autoComplete="name"
          autoCapitalize="words"
          maxLength={MAX_NAME}
          placeholder="Name…"
          className={fieldClass}
          aria-invalid={state.fieldErrors.name ? true : undefined}
          aria-describedby={nameErrorId}
        />
        {state.fieldErrors.name ? (
          <p id="contact-name-error" className="mt-2 text-sm text-error">
            {state.fieldErrors.name}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="contact-email" className="sr-only">
          Email
        </label>
        <input
          ref={emailRef}
          id="contact-email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          placeholder="Email…"
          className={fieldClass}
          aria-invalid={state.fieldErrors.email ? true : undefined}
          aria-describedby={emailErrorId}
        />
        {state.fieldErrors.email ? (
          <p id="contact-email-error" className="mt-2 text-sm text-error">
            {state.fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div className="relative">
        <label htmlFor="contact-message" className="sr-only">
          Message
        </label>
        <textarea
          ref={messageRef}
          id="contact-message"
          name="message"
          rows={7}
          maxLength={MAX_MESSAGE}
          placeholder="Message…"
          className={`${fieldClass} resize-y pb-10`}
          aria-invalid={state.fieldErrors.message ? true : undefined}
          aria-describedby={
            [messageErrorId, "contact-message-count"]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onChange={(event) => {
            setMessageLength(event.target.value.length);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return;
            }

            if (!event.metaKey && !event.ctrlKey) {
              return;
            }

            event.preventDefault();
            formRef.current?.requestSubmit();
          }}
        />
        <p
          id="contact-message-count"
          className="pointer-events-none absolute right-3 bottom-3 text-xs text-muted tabular-nums"
        >
          {messageLength}/{MAX_MESSAGE}
        </p>
        {state.fieldErrors.message ? (
          <p id="contact-message-error" className="mt-2 text-sm text-error">
            {state.fieldErrors.message}
          </p>
        ) : null}
      </div>

      <SubmitButton alerted={buttonAlert} shake={buttonShake} />
    </form>
  );
}
